import React, { useState, useEffect } from "react";

const SponsoredRowCalculator = ({ isAdmin }) => {
  const [minutes, setMinutes] = useState("");
  const [impressionsPerHour, setImpressionsPerHour] = useState("50000");
  const [cpm, setCpm] = useState("");
  const [breakEvenCost, setBreakEvenCost] = useState(null);
  const [showTimeConverter, setShowTimeConverter] = useState(false);
  const [hoursInput, setHoursInput] = useState("");
  const [daysInput, setDaysInput] = useState("");

  // ROI Calculator states
  const [budget, setBudget] = useState("");
  const [roiCpm, setRoiCpm] = useState("");
  const [ctr, setCtr] = useState("");
  const [averageEarnPerPlay, setAverageEarnPerPlay] = useState("");
  const [totalImpressions, setTotalImpressions] = useState(null);
  const [capturedImpressionsToPlays, setCapturedImpressionsToPlays] = useState(null);
  const [amountEarned, setAmountEarned] = useState(null);
  const [roi, setRoi] = useState(null);

  // Average Earn Per Play Calculator states
  const [minEstimatePayout, setMinEstimatePayout] = useState("");
  const [maxEstimatePayout, setMaxEstimatePayout] = useState("");
  const [totalPlays, setTotalPlays] = useState("");
  const [estimateEarnings, setEstimateEarnings] = useState(null);
  const [calculatedAverageEarnPerPlay, setCalculatedAverageEarnPerPlay] = useState(null);

  useEffect(() => {
    const minutesValue = parseFloat(minutes);
    const impressions = parseFloat(impressionsPerHour);
    const cpmValue = parseFloat(cpm);

    if (
      !isNaN(minutesValue) &&
      !isNaN(impressions) &&
      !isNaN(cpmValue) &&
      minutesValue > 0 &&
      impressions > 0 &&
      cpmValue > 0
    ) {
      // Calculate impressions for X minutes: (minutes / 60) * impressions per hour
      const impressionsForMinutes = (minutesValue / 60) * impressions;
      // Calculate cost: (Impressions / 1000) * CPM
      const cost = (impressionsForMinutes / 1000) * cpmValue;
      setBreakEvenCost(cost);
    } else {
      setBreakEvenCost(null);
    }
  }, [minutes, impressionsPerHour, cpm]);

  // ROI Calculator useEffect
  useEffect(() => {
    const budgetValue = parseFloat(budget);
    const cpmValue = parseFloat(roiCpm);
    const ctrValue = parseFloat(ctr);
    const earnPerPlay = parseFloat(averageEarnPerPlay);

    if (
      !isNaN(budgetValue) &&
      !isNaN(cpmValue) &&
      budgetValue > 0 &&
      cpmValue > 0
    ) {
      // Total impressions = (Budget / CPM) * 1000
      const impressions = (budgetValue / cpmValue) * 1000;
      setTotalImpressions(impressions);

      if (!isNaN(ctrValue) && ctrValue > 0 && ctrValue <= 100) {
        // Captured impressions to plays = Total impressions * (CTR / 100)
        const plays = impressions * (ctrValue / 100);
        setCapturedImpressionsToPlays(plays);

        if (!isNaN(earnPerPlay) && earnPerPlay > 0) {
          // Amount earned = Captured impressions to plays * average earn per play
          const earned = plays * earnPerPlay;
          setAmountEarned(earned);

          // ROI = ((Amount earned - Budget) / Budget) * 100
          const roiValue = ((earned - budgetValue) / budgetValue) * 100;
          setRoi(roiValue);
        } else {
          setAmountEarned(null);
          setRoi(null);
        }
      } else {
        setCapturedImpressionsToPlays(null);
        setAmountEarned(null);
        setRoi(null);
      }
    } else {
      setTotalImpressions(null);
      setCapturedImpressionsToPlays(null);
      setAmountEarned(null);
      setRoi(null);
    }
  }, [budget, roiCpm, ctr, averageEarnPerPlay]);

  // Average Earn Per Play Calculator useEffect - Update estimate earnings when min/max change
  useEffect(() => {
    const minPayout = parseFloat(minEstimatePayout);
    const maxPayout = parseFloat(maxEstimatePayout);

    if (
      !isNaN(minPayout) &&
      !isNaN(maxPayout) &&
      minPayout >= 0 &&
      maxPayout >= 0 &&
      maxPayout >= minPayout
    ) {
      // If estimate earnings is not set or is outside the new range, set it to average
      if (estimateEarnings === null || estimateEarnings < minPayout || estimateEarnings > maxPayout) {
        const averageEstimate = (minPayout + maxPayout) / 2;
        setEstimateEarnings(averageEstimate);
      }
    } else if (isNaN(minPayout) || isNaN(maxPayout)) {
      setEstimateEarnings(null);
    }
  }, [minEstimatePayout, maxEstimatePayout]);

  // Average Earn Per Play Calculator useEffect - Calculate average earn per play
  useEffect(() => {
    const plays = parseFloat(totalPlays);
    const earnings = estimateEarnings;

    if (
      !isNaN(plays) &&
      earnings !== null &&
      plays > 0 &&
      earnings >= 0
    ) {
      // Average earn per play = Estimate earnings / Total plays
      const avgEarnPerPlay = earnings / plays;
      setCalculatedAverageEarnPerPlay(avgEarnPerPlay);
    } else {
      setCalculatedAverageEarnPerPlay(null);
    }
  }, [estimateEarnings, totalPlays]);

  const handleMinutesChange = (e) => {
    setMinutes(e.target.value);
  };

  const handleImpressionsChange = (e) => {
    setImpressionsPerHour(e.target.value);
  };

  const handleCpmChange = (e) => {
    setCpm(e.target.value);
  };

  const handleAddTime = (e) => {
    e.preventDefault();
    const hours = parseFloat(hoursInput) || 0;
    const days = parseFloat(daysInput) || 0;
    
    // Convert hours and days to minutes
    const hoursToMinutes = hours * 60;
    const daysToMinutes = days * 24 * 60;
    const totalMinutesToAdd = hoursToMinutes + daysToMinutes;
    
    if (totalMinutesToAdd > 0) {
      const currentMinutes = parseFloat(minutes) || 0;
      const newMinutes = currentMinutes + totalMinutesToAdd;
      setMinutes(newMinutes.toString());
      setHoursInput("");
      setDaysInput("");
      setShowTimeConverter(false);
    }
  };

  const handleTimeConverterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAddTime(e);
    }
  };

  const handleUseCalculatedValue = () => {
    if (calculatedAverageEarnPerPlay !== null) {
      setAverageEarnPerPlay(calculatedAverageEarnPerPlay.toFixed(4));
    }
  };

  return (
    <div className="w-full flex flex-col lg:flex-row gap-6">
      {/* Break Even Calculator */}
      <div className="flex-1 min-w-0 bg-gray-900 text-white rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-800">
          <h2 className="text-lg font-bold text-fortpixel-gold">
            Sponsored Row Calculator
          </h2>
        </div>

        <div className="p-4">
          <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label htmlFor="minutes-input" className="text-sm font-medium">
                Minutes
              </label>
              <button
                type="button"
                onClick={() => setShowTimeConverter(!showTimeConverter)}
                className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                {showTimeConverter ? "Cancel" : "+ Add Hours/Days"}
              </button>
            </div>
            <input
              id="minutes-input"
              type="number"
              value={minutes}
              onChange={handleMinutesChange}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter minutes..."
              min="0"
            />
            {showTimeConverter && (
              <div className="mt-2 p-3 bg-gray-800 border border-gray-700 rounded-md">
                <form onSubmit={handleAddTime} className="space-y-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="hours-input" className="text-xs font-medium text-gray-300">
                      Hours
                    </label>
                    <input
                      id="hours-input"
                      type="number"
                      value={hoursInput}
                      onChange={(e) => setHoursInput(e.target.value)}
                      onKeyPress={handleTimeConverterKeyPress}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter hours..."
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="days-input" className="text-xs font-medium text-gray-300">
                      Days
                    </label>
                    <input
                      id="days-input"
                      type="number"
                      value={daysInput}
                      onChange={(e) => setDaysInput(e.target.value)}
                      onKeyPress={handleTimeConverterKeyPress}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter days..."
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 rounded-md text-sm font-medium transition-colors"
                  >
                    Add to Minutes
                  </button>
                </form>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="impressions-input" className="text-sm font-medium">
              Average Impressions Per Hour
            </label>
            <input
              id="impressions-input"
              type="number"
              value={impressionsPerHour}
              onChange={handleImpressionsChange}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter impressions per hour..."
              min="0"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="cpm-input" className="text-sm font-medium">
              CPM
            </label>
            <input
              id="cpm-input"
              type="number"
              value={cpm}
              onChange={handleCpmChange}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter CPM..."
              min="0"
              step="0.01"
            />
          </div>

          {breakEvenCost !== null && (
            <div className="space-y-3 mt-4">
              <div className="p-3 bg-gray-800 rounded-md">
                <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-medium">
                    Cost to Break Even at {minutes} minutes:
                  </span>
                  <span className="text-lg font-bold text-green-400">
                    ${breakEvenCost.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* ROI Calculator */}
      <div className="flex-1 min-w-0 bg-gray-900 text-white rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-800">
          <h2 className="text-lg font-bold text-fortpixel-gold">
            ROI Calculator
          </h2>
        </div>

        <div className="p-4">
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="budget-input" className="text-sm font-medium">
                Budget
              </label>
              <input
                id="budget-input"
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter budget..."
                min="0"
                step="0.01"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="roi-cpm-input" className="text-sm font-medium">
                CPM
              </label>
              <input
                id="roi-cpm-input"
                type="number"
                value={roiCpm}
                onChange={(e) => setRoiCpm(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter CPM..."
                min="0"
                step="0.01"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="ctr-input" className="text-sm font-medium">
                CTR (%)
              </label>
              <input
                id="ctr-input"
                type="number"
                value={ctr}
                onChange={(e) => setCtr(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter CTR percentage..."
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            {totalImpressions !== null && (
              <div className="p-3 bg-gray-800 rounded-md">
                <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-medium">
                    Total Estimated Impressions:
                  </span>
                  <span className="text-lg font-bold text-blue-400">
                    {totalImpressions.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="earn-per-play-input" className="text-sm font-medium">
                Average Earn Per Play
              </label>
              <input
                id="earn-per-play-input"
                type="number"
                value={averageEarnPerPlay}
                onChange={(e) => setAverageEarnPerPlay(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter average earn per play..."
                min="0"
                step="0.01"
              />
            </div>

            {(amountEarned !== null || capturedImpressionsToPlays !== null || roi !== null) && (
              <div className="space-y-3 mt-4">
                {amountEarned !== null && (
                  <div className="p-3 bg-gray-800 rounded-md">
                    <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm font-medium">
                        Amount Earned:
                      </span>
                      <span className="text-lg font-bold text-green-400">
                        ${amountEarned.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {roi !== null && (
                  <div className="p-3 bg-gray-800 rounded-md">
                    <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm font-medium">
                        ROI:
                      </span>
                      <span className={`text-lg font-bold ${roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {roi >= 0 ? '+' : ''}{roi.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                )}

                {capturedImpressionsToPlays !== null && (
                  <div className="p-3 bg-gray-800 rounded-md">
                    <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm font-medium">
                        Captured Impressions to Plays:
                      </span>
                      <span className="text-lg font-bold text-purple-400">
                        {capturedImpressionsToPlays.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Average Earn Per Play Calculator */}
      <div className="flex-1 min-w-0 bg-gray-900 text-white rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-800">
          <h2 className="text-lg font-bold text-fortpixel-gold">
            Average Earn Per Play Calculator
          </h2>
        </div>

        <div className="p-4">
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="min-payout-input" className="text-sm font-medium">
                Min Estimate Payout
              </label>
              <input
                id="min-payout-input"
                type="number"
                value={minEstimatePayout}
                onChange={(e) => setMinEstimatePayout(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter min estimate payout..."
                min="0"
                step="0.01"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="max-payout-input" className="text-sm font-medium">
                Max Estimate Payout
              </label>
              <input
                id="max-payout-input"
                type="number"
                value={maxEstimatePayout}
                onChange={(e) => setMaxEstimatePayout(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter max estimate payout..."
                min="0"
                step="0.01"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="total-plays-input" className="text-sm font-medium">
                Total Plays During That Time
              </label>
              <input
                id="total-plays-input"
                type="number"
                value={totalPlays}
                onChange={(e) => setTotalPlays(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter total plays..."
                min="0"
                step="1"
              />
            </div>

            {estimateEarnings !== null && 
             !isNaN(parseFloat(minEstimatePayout)) && 
             !isNaN(parseFloat(maxEstimatePayout)) &&
             parseFloat(minEstimatePayout) >= 0 &&
             parseFloat(maxEstimatePayout) >= 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-medium text-gray-300">
                    Min: ${parseFloat(minEstimatePayout).toFixed(2)}
                  </span>
                  <span className="text-sm font-medium text-fortpixel-gold">
                    Estimate Earnings: ${estimateEarnings.toFixed(2)}
                  </span>
                  <span className="text-sm font-medium text-gray-300">
                    Max: ${parseFloat(maxEstimatePayout).toFixed(2)}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    min={parseFloat(minEstimatePayout)}
                    max={parseFloat(maxEstimatePayout)}
                    value={estimateEarnings}
                    onChange={(e) => {
                      const min = parseFloat(minEstimatePayout);
                      const max = parseFloat(maxEstimatePayout);
                      const value = parseFloat(e.target.value);
                      const average = (min + max) / 2;
                      const range = max - min;
                      const snapThreshold = range * 0.02; // 2% of range for snapping
                      
                      // Snap to middle if within threshold
                      if (Math.abs(value - average) <= snapThreshold) {
                        setEstimateEarnings(average);
                      } else {
                        setEstimateEarnings(value);
                      }
                    }}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider relative z-10"
                    step="0.01"
                  />
                  {/* Average line indicator */}
                  <div
                    className="absolute top-0 h-2 w-0.5 bg-fortpixel-gold opacity-50 pointer-events-none z-0"
                    style={{
                      left: `calc(${((parseFloat(minEstimatePayout) + parseFloat(maxEstimatePayout)) / 2 - parseFloat(minEstimatePayout)) / (parseFloat(maxEstimatePayout) - parseFloat(minEstimatePayout)) * 100}% - 1px)`,
                    }}
                  ></div>
                </div>
                <style>{`
                  .slider::-webkit-slider-thumb {
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: #fbbf24;
                    cursor: pointer;
                    position: relative;
                    z-index: 20;
                  }
                  .slider::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: #fbbf24;
                    cursor: pointer;
                    border: none;
                    position: relative;
                    z-index: 20;
                  }
                `}</style>
              </div>
            )}

            {calculatedAverageEarnPerPlay !== null && (
              <div className="space-y-3 mt-4">
                <div className="p-3 bg-gray-800 rounded-md">
                  <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm font-medium">
                      Average Earn Per Play:
                    </span>
                    <span className="text-lg font-bold text-green-400">
                      ${calculatedAverageEarnPerPlay.toFixed(4)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleUseCalculatedValue}
                  className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium transition-colors"
                >
                  Use This Value in ROI Calculator
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsoredRowCalculator;

